import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistForm({ variant = "hero" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [position, setPosition] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), source: "landing" }),
      });

      if (!res.ok) throw new Error("Failed to join waitlist");

      const data = await res.json();
      setPosition(data.position);
      setSubmitted(true);

      if (data.is_new) {
        toast.success(`Welcome, ${data.name}! You're #${data.position} on the waitlist.`);
      } else {
        toast.info(`You're already on the list at position #${data.position}!`);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "final";

  if (submitted) {
    return (
      <div className={`w-full max-w-lg text-center py-6`} data-testid={`${variant}-waitlist-success`}>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-3 ${isDark ? "bg-bhyyve-honey/20 text-bhyyve-honey" : "bg-bhyyve-honeySoft text-bhyyve-honeyDeep"}`}>
          You're #{position} on the waitlist
        </div>
        <p className={`text-sm ${isDark ? "text-white/60" : "text-bhyyve-muted"}`}>
          Check your inbox for a confirmation email with your Founding Member details.
        </p>
      </div>
    );
  }

  if (isDark) {
    return (
      <div className="w-full max-w-lg" data-testid="final-waitlist-wrapper">
        <form onSubmit={handleSubmit} data-testid="final-waitlist-form" className="flex flex-col gap-3 p-3 rounded-3xl border bg-white/10 border-white/15 backdrop-blur-md">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              placeholder="Your name"
              data-testid="final-name-input"
              className="flex-1 h-12 px-5 rounded-full text-sm font-medium transition-all outline-none bg-white/10 border border-white/20 text-white placeholder:text-white/60 focus:bg-white/15 focus:border-white/40"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              placeholder="Email address"
              data-testid="final-email-input"
              className="flex-1 h-12 px-5 rounded-full text-sm font-medium transition-all outline-none bg-white/10 border border-white/20 text-white placeholder:text-white/60 focus:bg-white/15 focus:border-white/40"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="final-submit-button"
            className="group h-12 px-6 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all transform active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed bg-bhyyve-honey text-bhyyve-charcoal hover:bg-bhyyve-honeyDeep hover:text-white shadow-lg shadow-bhyyve-honey/30 hover:-translate-y-0.5"
          >
            {loading ? "Joining..." : "Join Waitlist"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </button>
        </form>
      </div>
    );
  }

  // Hero variant (light)
  return (
    <div className="w-full max-w-lg" data-testid="hero-waitlist-wrapper">
      <form onSubmit={handleSubmit} data-testid="hero-waitlist-form" className="flex flex-col gap-3 p-3 rounded-3xl border bg-white border-bhyyve-border shadow-[0_8px_30px_rgba(122,79,0,0.08)]">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            placeholder="Your name"
            data-testid="hero-name-input"
            className="flex-1 h-12 px-5 rounded-full text-sm font-medium transition-all outline-none bg-bhyyve-subtle/60 border border-transparent text-bhyyve-ink placeholder:text-bhyyve-muted/70 focus:bg-white focus:border-bhyyve-emerald/30 focus:ring-4 focus:ring-bhyyve-emerald/10"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            placeholder="Email address"
            data-testid="hero-email-input"
            className="flex-1 h-12 px-5 rounded-full text-sm font-medium transition-all outline-none bg-bhyyve-subtle/60 border border-transparent text-bhyyve-ink placeholder:text-bhyyve-muted/70 focus:bg-white focus:border-bhyyve-emerald/30 focus:ring-4 focus:ring-bhyyve-emerald/10"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="hero-submit-button"
          className="group h-12 px-6 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all transform active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed bg-bhyyve-charcoal text-bhyyve-honey hover:bg-bhyyve-emerald hover:text-white shadow-lg shadow-bhyyve-charcoal/25 hover:-translate-y-0.5"
        >
          {loading ? "Joining..." : "Join Waitlist"}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
      </form>
    </div>
  );
}
